import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Flame } from 'lucide-react';
import { getMe, getValidAccessToken } from '@/services/authService';
import type { UserProfile } from '@/services/authService';
import { useGoalModal } from '@/features/profile/contexts/GoalModalContext';
import { useStreakData } from '@/features/learning/hooks/useStreakData';

const toPersianDigits = (num: number) =>
  String(num).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const MetricBoxes: React.FC = () => {
  const navigate = useNavigate();
  const { openDailyGoalModal } = useGoalModal();
  const { weeklyProgress, currentStreak, todayMinutes, isLoading: isStreakLoading } = useStreakData();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = await getValidAccessToken();
        if (!token) {
          setIsLoading(false);
          return;
        }

        // Fetch both profile and streak info in parallel
        const userProfile = await getMe(); /*
          getMe(),
          legacyRequestRemoved();
            console.warn('⚠️ [MetricBoxes] Failed to load streak info', e);
            return null;
          })
        ]); */

        if (mounted) {
          if (userProfile) setProfile(userProfile);
        }
      } catch (e) {
        console.warn('⚠️ [MetricBoxes] Failed to load data', e);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Listen for goal updates
  useEffect(() => {
    const handleGoalUpdate = async () => {
      try {
        const userProfile = await getMe();
        if (userProfile) setProfile(userProfile);
      } catch (e) {
        console.warn('⚠️ [MetricBoxes] Failed to reload profile', e);
      }
    };

    window.addEventListener('goalUpdated', handleGoalUpdate);
    return () => window.removeEventListener('goalUpdated', handleGoalUpdate);
  }, []);

  // Extract values from profile
  const dailyGoal = profile?.daily_goal ?? 0;
  const streakGoal = profile?.streak_goal ?? 3;

  const streak = currentStreak;

  // Calculate daily progress
  const dailyProgress = dailyGoal > 0 ? (todayMinutes / dailyGoal) * 100 : 0;

  if (isLoading || isStreakLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:gap-4" dir="rtl">
        {/* Daily Goal Shimmer */}
        <div className="relative overflow-hidden rounded-3xl p-3 sm:p-4 h-[180px] bg-gray-200">
          <div className="animate-pulse space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gray-300" />
              <div className="h-4 w-20 rounded bg-gray-300" />
            </div>
            <div className="flex items-baseline gap-1 mt-4">
              <div className="h-10 w-16 rounded bg-gray-300" />
              <div className="h-4 w-10 rounded bg-gray-300" />
            </div>
            <div className="space-y-2 mt-4">
              <div className="flex justify-between">
                <div className="h-3 w-24 rounded bg-gray-300" />
                <div className="h-3 w-16 rounded bg-gray-300" />
              </div>
              <div className="h-2.5 w-full rounded-full bg-gray-300" />
            </div>
          </div>
        </div>
        {/* Streak Goal Shimmer */}
        <div className="relative overflow-hidden rounded-3xl p-3 sm:p-4 h-[180px] bg-gray-200">
          <div className="animate-pulse space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gray-300" />
              <div className="h-4 w-20 rounded bg-gray-300" />
            </div>
            <div className="flex items-baseline gap-1 mt-4">
              <div className="h-10 w-12 rounded bg-gray-300" />
              <div className="h-4 w-8 rounded bg-gray-300" />
            </div>
            <div className="space-y-2 mt-4">
              <div className="flex justify-between">
                <div className="h-3 w-20 rounded bg-gray-300" />
                <div className="h-3 w-12 rounded bg-gray-300" />
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 7 }).map((_, index) => (
                  <div key={index} className="h-8 rounded-lg bg-gray-300" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4" dir="rtl">
      {/* Daily Goal Box */}
      <div 
        onClick={openDailyGoalModal}
        className="relative overflow-visible rounded-3xl p-3 sm:p-4 transition-all duration-300 hover:scale-[1.02] cursor-pointer group ring-1 ring-black/10 bg-[#FFFDFC]"
        style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}
      >
        {/* Background */}
        <div className="absolute inset-0 rounded-3xl bg-[#FFFDFC]" />

        {/* Content Container */}
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-[#6A5BE2]/10 shadow-sm">
              <Target className="w-4 h-4 text-[#6A5BE2]" />
            </div>
            <h3 className="font-persian text-xs sm:text-sm text-black font-bold">هدف روزانه</h3>
          </div>

          <div className="flex items-baseline gap-1 mb-4">
            <span className="font-bold text-3xl sm:text-4xl text-black tracking-tight">
              {toPersianDigits(todayMinutes)}
            </span>
            <span className="font-persian text-sm sm:text-base text-black font-medium">دقیقه</span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs sm:text-sm px-0.5">
              <span className="font-persian text-black font-medium">هدف مطالعه روزانه</span>
              <span className="font-bold text-black font-persian">{toPersianDigits(dailyGoal || 0)} دقیقه</span>
            </div>

            {/* Progress Bar */}
            <div className="relative h-3.5 bg-[#6A5BE2]/15 rounded-full overflow-hidden shadow-inner">
              <div
                className="absolute right-0 top-0 bottom-0 rounded-full transition-all duration-1000 ease-out shadow-sm"
                style={{ width: `${Math.min(dailyProgress, 100)}%`, background: '#6A5BE2' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Streak Goal Box - "هدف پیوستگی" */}
      <div
        onClick={() => navigate('/learning/streak-calendar')}
        className="relative overflow-visible rounded-3xl p-3 sm:p-4 transition-all duration-300 hover:scale-[1.02] cursor-pointer group ring-1 ring-black/10 bg-[#FFFDFC]"
        style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}
      >
        {/* Background */}
        <div className="absolute inset-0 rounded-3xl bg-[#FFFDFC]" />

        {/* Content Container */}
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-[#F97316]/10 shadow-sm">
              <Flame className="w-4 h-4 text-[#F97316]" />
            </div>
            <h3 className="font-persian text-xs sm:text-sm text-black font-bold">هدف پیوستگی</h3>
          </div>

          <div className="flex items-baseline gap-1 mb-4">
            <span className="font-bold text-3xl sm:text-4xl text-black tracking-tight">
              {toPersianDigits(streak)}
            </span>
            <span className="font-persian text-sm sm:text-base text-black font-medium">روز</span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs sm:text-sm px-0.5">
              <span className="font-persian text-black font-medium">هدف پیوستگی</span>
              <span className="font-bold text-black font-persian">{toPersianDigits(streakGoal)}/{toPersianDigits(Math.min(streak, streakGoal))}</span>
            </div>

            {/* Weekly streak dots: Saturday through Friday */}
            <div className="grid grid-cols-7 gap-1.5">
              {weeklyProgress.map((day, index) => {
                const isFilled = day.isCompleted || day.isFreeze;
                return (
                  <div key={`${day.day}-${index}`} className="flex flex-col items-center gap-0.5">
                    <div
                      className={`relative -top-1 w-[18px] h-[18px] rounded-full transition-colors duration-300 shadow-inner ${
                        isFilled ? 'bg-[#F97316]' : 'bg-[#F97316]/15'
                      }`}
                      aria-label={isFilled ? 'روز مطالعه شده' : 'روز بدون مطالعه'}
                    />
                    <span className="text-[10px] font-medium text-black leading-none">{day.day}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricBoxes;
